'use client'

import { ClientAppointmentView } from '@/lib/api/client-portal/client-portal-api'
import {
    BILLING_STATUS_BADGE,
    BILLING_STATUS_LABELS,
} from '@/lib/billing/payment-labels'
import { mask, unMask } from '@/lib/form-state/util'
import { format } from 'date-fns'
import { ReactNode, useMemo, useState } from 'react'
import {
    MdAccountBalanceWallet,
    MdCalendarToday,
    MdContentCopy,
    MdExpandLess,
    MdExpandMore,
    MdInventory2,
    MdLocalHospital,
    MdLocationOn,
    MdMedicalServices,
    MdPerson,
    MdScience,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const formatCents = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

const currencyMask = (value: string): string => {
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (!digits) return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCents = (value: string): number => {
    if (!value.trim()) return 0
    return parseInt(unMask(value, 'currency')) || 0
}

/** Data de hoje em YYYY-MM-DD, no fuso local (não UTC). */
const todayIso = (): string => format(new Date(), 'yyyy-MM-dd')

/** Rótulos amigáveis dos status do procedimento (o cliente não vê enum cru). */
const PROCEDURE_STATUS_LABELS: Record<string, string> = {
    SCHEDULED: 'Agendado',
    IN_PROGRESS: 'Em andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
    BUDGET: 'Orçamento',
}

/**
 * Bloco de informação COLAPSÁVEL. Fechado por padrão: o pagamento é o que
 * importa, então os detalhes do atendimento ficam recolhidos para não empurrar o
 * pagamento para baixo — o cliente abre se quiser conferir.
 */
function InfoSection({
    icon,
    title,
    children,
    defaultOpen = false,
}: {
    icon: ReactNode
    title: string
    children: ReactNode
    defaultOpen?: boolean
}) {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-2 p-4 text-left hover:bg-base-200/40 transition-colors"
            >
                <span className="text-secondary">{icon}</span>
                <h2 className="font-semibold flex-1">{title}</h2>
                {open ? (
                    <MdExpandLess className="text-xl text-base-content/40" />
                ) : (
                    <MdExpandMore className="text-xl text-base-content/40" />
                )}
            </button>
            {open && <div className="px-5 pb-5 -mt-1">{children}</div>}
        </div>
    )
}

/** Botão que copia o link público para a área de transferência. */
function ShareLink({ url }: { url: string }) {
    const [copied, setCopied] = useState(false)
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            toast.success('Link copiado')
            setTimeout(() => setCopied(false), 2000)
        } catch {
            toast.error('Não foi possível copiar o link')
        }
    }
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
            <h2 className="font-semibold mb-1">Link de acompanhamento</h2>
            <p className="text-sm text-base-content/60 mb-3">
                Compartilhe este link com quem for ajudar no pagamento. Ao
                abrir, basta confirmar o CPF do paciente — não precisa de login.
            </p>
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    readOnly
                    value={url}
                    onFocus={e => e.currentTarget.select()}
                    className="input input-bordered flex-1 text-sm"
                />
                <button
                    type="button"
                    onClick={copy}
                    className="btn btn-secondary gap-1"
                >
                    <MdContentCopy className="text-base" />
                    {copied ? 'Copiado' : 'Copiar'}
                </button>
            </div>
        </div>
    )
}

/**
 * Apresentação do detalhe de um procedimento flex, compartilhada entre o portal
 * logado (`/client/:id`) e o link público (`/p/:token`). Recebe a visão curada
 * pronta e um handler de pagamento — quem carrega os dados e como paga difere
 * entre os dois, mas a tela é idêntica.
 *
 * `onCreatePayment` retorna o token do checkout da cobrança criada (ou `null` na
 * falha). No sucesso o formulário é limpo e a tela redireciona direto para o
 * checkout `/pay/<token>`. `shareUrl` mostra o bloco de compartilhamento.
 */
export default function OrderDetailView({
    view,
    onCreatePayment,
    headerSlot,
    shareUrl,
}: {
    view: ClientAppointmentView
    onCreatePayment: (
        amountCents: number,
        dueDateIso: string
    ) => Promise<string | null>
    headerSlot?: ReactNode
    shareUrl?: string | null
}) {
    const [amount, setAmount] = useState('')
    const [dueDate, setDueDate] = useState(todayIso())
    const [submitting, setSubmitting] = useState(false)

    // "Falta" = total − pago (só o pago abate). O teto para GERAR nova cobrança é
    // o que ainda não foi lançado (evita empilhar cobranças acima do total).
    const remaining = view.remaining ?? 0
    const remainingToBill = view.remainingToBill ?? remaining
    const isFlex = !!view.flexPayment
    const amountCents = parseCents(amount)

    const amountError = useMemo(() => {
        if (!amount) return null
        if (amountCents < 100) return 'O valor mínimo é R$ 1,00.'
        if (amountCents > remainingToBill)
            return `O valor não pode passar do saldo em aberto (${formatCents(remainingToBill)}).`
        return null
    }, [amount, amountCents, remainingToBill])

    const handlePay = async (e: React.FormEvent) => {
        e.preventDefault()
        if (amountError || amountCents <= 0) return
        setSubmitting(true)
        try {
            const checkoutToken = await onCreatePayment(amountCents, dueDate)
            if (checkoutToken) {
                setAmount('')
                setDueDate(todayIso())
                // Vai direto para o checkout da cobrança recém-gerada. `from=flex`
                // ajusta o rótulo do botão de retorno; `return_url` (esta mesma
                // página) faz o checkout voltar para cá após a confirmação.
                const returnUrl = encodeURIComponent(window.location.href)
                window.location.href = `/pay/${checkoutToken}?from=flex&return_url=${returnUrl}`
                return
            }
        } finally {
            setSubmitting(false)
        }
    }

    const addr = view.address
    const addressLine = addr
        ? [
              addr.street,
              addr.number,
              addr.complement,
              addr.neighborhood,
              addr.city && addr.state
                  ? `${addr.city}/${addr.state}`
                  : addr.city || addr.state,
              addr.zipCode,
          ]
              .filter(Boolean)
              .join(', ')
        : null

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
                {headerSlot}
                <div className="flex-1 min-w-0">
                    {view.clientName && (
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-secondary truncate">
                            <MdPerson className="text-base shrink-0" />
                            {view.clientName}
                        </p>
                    )}
                    <h1 className="text-xl font-bold text-base-content truncate">
                        {view.title}
                    </h1>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                        <span className="badge badge-ghost badge-sm">
                            {PROCEDURE_STATUS_LABELS[view.status] ||
                                view.status}
                        </span>
                        {view.flexPayment && (
                            <span className="badge badge-info badge-sm gap-1">
                                <MdAccountBalanceWallet className="text-xs" />
                                Pagamento Flex
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Detalhes do atendimento — renderizados DEPOIS do pagamento
                (order-last), cada bloco colapsável e fechado por padrão, para o
                pagamento flex ganhar o topo da tela. */}
            <div className="order-last flex flex-col gap-4">
                {/* Agendamento */}
                <InfoSection
                    icon={<MdCalendarToday className="text-xl" />}
                    title="Agendamento"
                >
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div className="flex justify-between gap-2">
                            <dt className="text-base-content/60">Data</dt>
                            <dd className="font-medium text-right">
                                {view.scheduledAt
                                    ? view.arrivalOrder
                                        ? format(
                                              new Date(view.scheduledAt),
                                              'dd/MM/yyyy'
                                          ) + ' (por ordem de chegada)'
                                        : format(
                                              new Date(view.scheduledAt),
                                              "dd/MM/yyyy 'às' HH:mm"
                                          )
                                    : 'A definir'}
                            </dd>
                        </div>
                        {view.duration ? (
                            <div className="flex justify-between gap-2">
                                <dt className="text-base-content/60">
                                    Duração
                                </dt>
                                <dd className="font-medium text-right">
                                    {view.duration} min
                                </dd>
                            </div>
                        ) : null}
                        {view.healthPlan && (
                            <div className="flex justify-between gap-2">
                                <dt className="text-base-content/60">
                                    Plano de saúde
                                </dt>
                                <dd className="font-medium text-right">
                                    {view.healthPlan.name}
                                </dd>
                            </div>
                        )}
                        {view.room && (
                            <div className="flex justify-between gap-2">
                                <dt className="text-base-content/60">Sala</dt>
                                <dd className="font-medium text-right">
                                    {view.room}
                                </dd>
                            </div>
                        )}
                        {view.completedAt && (
                            <div className="flex justify-between gap-2">
                                <dt className="text-base-content/60">
                                    Concluído em
                                </dt>
                                <dd className="font-medium text-right">
                                    {format(
                                        new Date(view.completedAt),
                                        'dd/MM/yyyy'
                                    )}
                                </dd>
                            </div>
                        )}
                    </dl>
                </InfoSection>

                {/* Profissionais */}
                {(view.professional || view.team.length > 0) && (
                    <InfoSection
                        icon={<MdPerson className="text-xl" />}
                        title="Profissionais"
                    >
                        <ul className="flex flex-col gap-1 text-sm">
                            {view.professional && (
                                <li className="flex items-center justify-between gap-2">
                                    <span className="font-medium">
                                        {view.professional.name}
                                    </span>
                                    {view.professional.registrationNumber && (
                                        <span className="text-base-content/60">
                                            {
                                                view.professional
                                                    .registrationNumber
                                            }
                                        </span>
                                    )}
                                </li>
                            )}
                            {view.team.map((t, i) => (
                                <li
                                    key={i}
                                    className="flex items-center justify-between gap-2"
                                >
                                    <span>{t.name}</span>
                                    {t.registrationNumber && (
                                        <span className="text-base-content/60">
                                            {t.registrationNumber}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </InfoSection>
                )}

                {/* Local / parceiro / hospital */}
                {(view.partner || view.hospital || addressLine) && (
                    <InfoSection
                        icon={<MdLocationOn className="text-xl" />}
                        title="Local de atendimento"
                    >
                        <div className="flex flex-col gap-1 text-sm">
                            {view.partner && (
                                <p className="flex items-center gap-2">
                                    <MdLocalHospital className="text-base-content/50" />
                                    {view.partner.name}
                                </p>
                            )}
                            {view.hospital && (
                                <p className="flex items-center gap-2">
                                    <MdLocalHospital className="text-base-content/50" />
                                    {view.hospital.name}
                                </p>
                            )}
                            {addressLine && (
                                <p className="text-base-content/70">
                                    {addressLine}
                                </p>
                            )}
                        </div>
                    </InfoSection>
                )}

                {/* Procedimentos empilhados. Num pacote esta lista some: o item é o
                    próprio pacote, cujo nome já está no topo. */}
                {view.items.length > 0 && (
                    <InfoSection
                        icon={<MdMedicalServices className="text-xl" />}
                        title={
                            view.isPackage ? 'Itens do pacote' : 'Procedimentos'
                        }
                    >
                        <ul className="flex flex-col gap-1 text-sm">
                            {view.items.map((it, i) => (
                                <li
                                    key={i}
                                    className="flex items-center justify-between gap-2"
                                >
                                    <span>{it.name}</span>
                                    {it.quantity > 1 && (
                                        <span className="text-base-content/60">
                                            {it.quantity}x
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </InfoSection>
                )}

                {/* Atrelados ao atendimento. Num PACOTE isto não são exames: são os
                    componentes (a cirurgia, a consulta do anestesista, o retorno) — o
                    título "Exames" desmentia a lista logo abaixo dele. */}
                {view.exams.length > 0 && (
                    <InfoSection
                        icon={<MdScience className="text-xl" />}
                        title={view.isPackage ? 'O que está incluso' : 'Exames'}
                    >
                        <ul className="flex flex-col gap-1 text-sm list-disc list-inside">
                            {view.exams.map((e, i) => (
                                <li key={i}>{e.name}</li>
                            ))}
                        </ul>
                    </InfoSection>
                )}

                {/* Materiais — nome e quantidade, sem valores */}
                {view.supplies.length > 0 && (
                    <InfoSection
                        icon={<MdInventory2 className="text-xl" />}
                        title="Materiais"
                    >
                        <ul className="flex flex-col gap-1 text-sm">
                            {view.supplies.map((s, i) => (
                                <li
                                    key={i}
                                    className="flex items-center justify-between gap-2"
                                >
                                    <span>{s.name}</span>
                                    <span className="text-base-content/60">
                                        {s.quantity}
                                        {s.unit ? ` ${s.unit}` : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </InfoSection>
                )}

                {/* Instruções ao paciente */}
                {(view.preInstructions || view.postInstructions) && (
                    <InfoSection
                        icon={<MdMedicalServices className="text-xl" />}
                        title="Instruções"
                    >
                        <div className="flex flex-col gap-3 text-sm">
                            {view.preInstructions && (
                                <div>
                                    <p className="font-medium mb-1">
                                        {view.isPackage
                                            ? 'Antes do atendimento'
                                            : 'Antes do procedimento'}
                                    </p>
                                    <p className="text-base-content/70 whitespace-pre-wrap">
                                        {view.preInstructions}
                                    </p>
                                </div>
                            )}
                            {view.postInstructions && (
                                <div>
                                    <p className="font-medium mb-1">
                                        {view.isPackage
                                            ? 'Após o atendimento'
                                            : 'Após o procedimento'}
                                    </p>
                                    <p className="text-base-content/70 whitespace-pre-wrap">
                                        {view.postInstructions}
                                    </p>
                                </div>
                            )}
                        </div>
                    </InfoSection>
                )}
            </div>
            {/* fim dos detalhes (order-last) */}

            {/* Saldo (só total, sem discriminar) */}
            {isFlex && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <MdAccountBalanceWallet className="text-xl text-secondary" />
                        <h2 className="font-semibold">Pagamento Flex</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <p className="text-xs text-base-content/60">
                                Valor total
                            </p>
                            <p className="text-lg font-bold">
                                {formatCents(view.finalPrice)}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60">
                                Já pago
                            </p>
                            <p className="text-lg font-bold text-success">
                                {formatCents(view.paidSoFar)}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60">
                                Falta
                            </p>
                            <p className="text-lg font-bold text-warning">
                                {formatCents(view.remaining)}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Link de compartilhamento (quando disponível) */}
            {isFlex && shareUrl && <ShareLink url={shareUrl} />}

            {/* Novo pagamento — só quando ainda há saldo a LANÇAR (não o que falta
                pagar): se tudo já foi gerado em cobranças, o paciente paga as
                pendentes em vez de gerar novas. */}
            {isFlex && remainingToBill > 0 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                    <h2 className="font-semibold mb-1">Fazer um pagamento</h2>
                    <p className="text-sm text-base-content/60 mb-4">
                        Escolha quanto quer pagar agora e a data de vencimento.
                        Você pode pagar em quantas vezes quiser, até quitar o
                        valor total.
                    </p>
                    <form
                        onSubmit={handlePay}
                        className="flex flex-wrap items-start gap-3"
                    >
                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">
                                    Valor
                                </span>
                            </label>
                            <input
                                type="text"
                                className={`input input-bordered w-44 ${
                                    amountError ? 'input-error' : ''
                                }`}
                                value={amount}
                                onChange={e =>
                                    setAmount(currencyMask(e.target.value))
                                }
                                placeholder="R$ 0,00"
                                inputMode="numeric"
                            />
                            <label className="label py-1">
                                <span
                                    className={`label-text-alt ${
                                        amountError
                                            ? 'text-error'
                                            : 'text-base-content/60'
                                    }`}
                                >
                                    {amountError ||
                                        `Até ${formatCents(remainingToBill)}`}
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label py-1">
                                <span className="label-text text-sm font-medium">
                                    Vencimento
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-44"
                                value={dueDate}
                                min={todayIso()}
                                onChange={e => setDueDate(e.target.value)}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-secondary mt-8"
                            disabled={
                                submitting || !!amountError || amountCents <= 0
                            }
                        >
                            {submitting && (
                                <span className="loading loading-spinner loading-sm" />
                            )}
                            Gerar pagamento
                        </button>
                    </form>
                </div>
            )}

            {/* Tudo pago: encerrado. */}
            {isFlex && remaining <= 0 && (
                <div className="rounded-xl border border-success/40 bg-success/10 p-4">
                    <p className="font-medium">
                        {view.isPackage
                            ? 'Este pacote já está totalmente pago.'
                            : 'Este procedimento já está totalmente pago.'}
                    </p>
                </div>
            )}

            {/* Nada a gerar, mas ainda falta pagar: há cobranças em aberto. */}
            {isFlex && remaining > 0 && remainingToBill <= 0 && (
                <div className="rounded-xl border border-warning/40 bg-warning/10 p-4">
                    <p className="font-medium">
                        Todo o valor já foi lançado em cobranças. Pague as
                        cobranças em aberto abaixo para concluir.
                    </p>
                </div>
            )}

            {/* Pagamentos gerados */}
            {view.charges.length > 0 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-5">
                    <h2 className="font-semibold mb-3">Pagamentos</h2>
                    <div className="flex flex-col gap-2">
                        {view.charges.map(c => (
                            <div
                                key={c.id}
                                className="flex items-center gap-3 p-3 rounded-lg border border-base-300/50"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium">
                                            {formatCents(c.amount ?? 0)}
                                        </span>
                                        <span
                                            className={`badge badge-sm ${
                                                BILLING_STATUS_BADGE[
                                                    c.status
                                                ] || 'badge-ghost'
                                            }`}
                                        >
                                            {BILLING_STATUS_LABELS[c.status] ||
                                                c.status}
                                        </span>
                                    </div>
                                    {c.status === 'PAID' && c.paidAt ? (
                                        <p className="text-xs text-success mt-0.5">
                                            Pago em{' '}
                                            {format(
                                                new Date(c.paidAt),
                                                'dd/MM/yyyy'
                                            )}
                                        </p>
                                    ) : (
                                        c.dueDate && (
                                            <p className="text-xs text-base-content/60 mt-0.5">
                                                Vence em{' '}
                                                {format(
                                                    new Date(c.dueDate),
                                                    'dd/MM/yyyy'
                                                )}
                                            </p>
                                        )
                                    )}
                                </div>
                                {/* Pagar abre o checkout na MESMA janela (não em
                                    aba nova), com from=flex + return_url para
                                    voltar para cá após a confirmação. Só para
                                    PENDING/OVERDUE, os mesmos status que o
                                    checkout aceita: estornada ou baixada com
                                    botão "Pagar" parecia dívida em aberto. */}
                                {(c.status === 'PENDING' ||
                                    c.status === 'OVERDUE') &&
                                    c.publicToken && (
                                        <a
                                            href={`/pay/${c.publicToken}?from=flex&return_url=${encodeURIComponent(
                                                typeof window !== 'undefined'
                                                    ? window.location.href
                                                    : ''
                                            )}`}
                                            className="btn btn-secondary btn-sm gap-1"
                                        >
                                            Pagar
                                        </a>
                                    )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!isFlex && (
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-5">
                    <p className="text-sm text-base-content/70">
                        Este atendimento não está em pagamento Flex. Em caso de
                        dúvida sobre valores, procure a um atendente.
                    </p>
                </div>
            )}
        </div>
    )
}
